// Applying a PushDelta to an Audiotool document — the only code that turns
// reconciler ops into entities.
//
// One transaction per batch (transact.ts: never doc.modify()). Inside it each
// op is applied on its own and a failure is caught, recorded and skipped:
// validation throws at create()/update() and leaves the builder usable (spike
// D3), so one bad region never costs the rest of the batch. The LEDGER is
// updated only for ops that landed, so a failed op is simply retried by the
// next push.
//
// Entity recipes, all from the spike (docs/project/audiotool-entities.md):
//   config        groove first, then config{defaultGroove} (A8)
//   audio lane    audioDevice{gain 1} -> audioTrack{player}, mixerChannel,
//                 desktopAudioCable device.audioOutput -> strip.audioInput —
//                 insertSample's own recipe (TR1). The device stays at unity
//                 so the strip alone carries Live's fader.
//   MIDI lane     the same with a GM instrument on Audiotool's gakki sampler
//                 as the player — a drum kit for a percussion lane, a piano
//                 otherwise (instruments.ts), resolved before the transaction
//                 because presets come over the network. Live's instrument
//                 can't come across, so this is a stand-in the user swaps; a
//                 Heisenberg when no preset could be fetched (TR4).
//   audio region  insertSample{attachTo, region, sample{musicDurationTicks,
//                 offsetTicks}, loop} (A6, IS0–IS7), then t.update for
//                 isEnabled/displayName and zero fades — insertSample honours
//                 only position and duration itself (A1), and its default
//                 10-tick fades would put audible dips between sliced clips.
//   note region   noteCollection, noteRegion{track, collection, region with
//                 loopDuration = duration so it plays once}, one note each (A7).
// Removal takes the region's sample/automation (audio) or its whole collection
// (notes) with it — removeWithDependencies only follows pointers INTO an
// entity, so a removed audio region would otherwise orphan its sample and
// playback automation, one pair per edit, forever.

import type { NexusEntity, TransactionBuilder } from "@audiotool/nexus/document";

import type {
  LedgerTrack,
  PushDelta,
  PushLedger,
  PushOp,
  RegionIntent,
  SampleLibrary,
  TrackAttrs,
} from "../../../shared/audiotool/push-reconcile.js";
import type { GakkiPreset } from "./instruments.js";
import { transact, type NexusDoc, type TransactOptions } from "./transact.js";

/** Heisenberg: a polyphonic synth, so any note list plays. The fallback when
 * no GM preset could be fetched — see instruments.ts. */
export const MIDI_STAND_IN_INSTRUMENT = "heisenberg";

/** What a GM preset creates. Named so the reader/writer agree on the type. */
export const MIDI_PRESET_INSTRUMENT = "gakki";

const EMPTY_PRESETS: ReadonlyMap<string, GakkiPreset> = new Map();

export interface OpFailure {
  op: PushOp;
  error: string;
}

export interface WriteResult {
  applied: number;
  failures: OpFailure[];
}

/**
 * Apply every batch of `delta` (uploads must already be in `library`), updating
 * `ledger` in place for each op that lands.
 */
export async function applyDelta(
  doc: NexusDoc,
  delta: PushDelta,
  ledger: PushLedger,
  library: SampleLibrary,
  opts: TransactOptions & { instruments?: ReadonlyMap<string, GakkiPreset> } = {},
): Promise<WriteResult> {
  const result: WriteResult = { applied: 0, failures: [] };
  for (const batch of delta.batches) {
    await transact(
      doc,
      (t) => {
        for (const op of batch) {
          try {
            applyOp(t, op, ledger, library, opts.instruments ?? EMPTY_PRESETS);
            result.applied++;
          } catch (e) {
            result.failures.push({ op, error: e instanceof Error ? e.message : String(e) });
          }
        }
      },
      opts,
    );
  }
  return result;
}

/**
 * "mixerMaster — singleton; required for sound" (docs/audiotool-nexus/entities.md).
 * The Studio adds one when a project is first opened there; a project only the
 * SDK has written has none, and Audiotool renders it silent (checked with
 * tools/audiotool-render-spike.ts). Every channel we create needs one to reach.
 */
function ensureMixerMaster(t: TransactionBuilder): void {
  if (t.entities.ofTypes("mixerMaster").get().length === 0) t.create("mixerMaster", {});
}

function applyOp(
  t: TransactionBuilder,
  op: PushOp,
  ledger: PushLedger,
  library: SampleLibrary,
  instruments: ReadonlyMap<string, GakkiPreset>,
): void {
  switch (op.op) {
    case "ensureConfig": {
      if (t.entities.ofTypes("config").get().length > 0) return; // someone beat us to it
      const groove = t.create("groove", {});
      t.create("config", { tempoBpm: op.tempoBpm, defaultGroove: groove.location });
      ensureMixerMaster(t);
      return;
    }
    case "setTempo":
      t.update(config(t).fields.tempoBpm, op.bpm);
      ledger.song.tempoBpm = op.bpm;
      return;
    case "setSignature": {
      const c = config(t);
      t.update(c.fields.signatureNumerator, op.numerator);
      t.update(c.fields.signatureDenominator, op.denominator);
      ledger.song.signature = { numerator: op.numerator, denominator: op.denominator };
      return;
    }
    case "growDuration": {
      const c = config(t);
      if (op.durationTicks > c.fields.durationTicks.value) t.update(c.fields.durationTicks, op.durationTicks);
      return;
    }
    case "createTrack":
      ledger.tracks[op.laneKey] = createLane(t, op.trackKind, op.order, op.attrs, instruments.get(op.laneKey));
      return;
    case "updateTrack": {
      const lt = ledger.tracks[op.laneKey];
      if (!lt) throw new Error(`no ledger track for ${op.laneKey}`);
      setLaneAttrs(t, lt, op.attrs);
      lt.attrs = op.attrs;
      return;
    }
    case "removeTrack": {
      const lt = ledger.tracks[op.laneKey];
      if (lt?.playerId && t.entities.has(lt.playerId)) t.removeWithDependencies(lt.playerId); // track + cable
      if (lt?.stripId && t.entities.has(lt.stripId)) t.removeWithDependencies(lt.stripId);
      if (t.entities.has(op.trackId)) t.removeWithDependencies(op.trackId);
      delete ledger.tracks[op.laneKey];
      return;
    }
    case "forgetTrack":
      delete ledger.tracks[op.laneKey];
      ledger.yieldedLanes[op.laneKey] = true;
      return;
    case "createRegion": {
      const lt = ledger.tracks[op.intent.laneKey];
      if (!lt) throw new Error(`no track for lane ${op.intent.laneKey}`);
      const regionId = op.intent.kind === "audioRegion"
        ? createAudioRegion(t, lt, op.intent, library)
        : createNoteRegion(t, lt, op.intent);
      ledger.regions[op.intent.key] = {
        key: op.intent.key,
        laneKey: op.intent.laneKey,
        kind: op.intent.kind === "audioRegion" ? "audio" : "note",
        regionId,
        fp: op.intent.fp,
        observed: { positionTicks: op.intent.positionTicks, durationTicks: op.intent.durationTicks },
      };
      delete ledger.yieldedRegions[op.intent.key];
      return;
    }
    case "updateRegion": {
      updateRegion(t, op.regionId, op.intent);
      const prev = ledger.regions[op.fromKey];
      if (!prev) throw new Error(`no ledger region for ${op.fromKey}`);
      delete ledger.regions[op.fromKey];
      ledger.regions[op.intent.key] = {
        ...prev,
        key: op.intent.key,
        fp: op.intent.fp,
        observed: { positionTicks: op.intent.positionTicks, durationTicks: op.intent.durationTicks },
      };
      return;
    }
    case "removeRegion":
      removeRegion(t, op.regionId);
      delete ledger.regions[op.key];
      return;
    case "forgetRegion":
      delete ledger.regions[op.key];
      ledger.yieldedRegions[op.key] = op.contentFp;
      return;
  }
}

function config(t: TransactionBuilder): NexusEntity<"config"> {
  const c = t.entities.ofTypes("config").getOne();
  if (!c) throw new Error("the project has no config entity");
  return c;
}

function nextStripOrder(t: TransactionBuilder): number {
  const orders = t.entities.ofTypes("mixerChannel").get().map((s) => s.fields.displayParameters.fields.orderAmongStrips.value);
  return orders.length > 0 ? Math.max(...orders) + 1 : 0;
}

function createLane(
  t: TransactionBuilder,
  kind: "audio" | "note",
  order: number,
  attrs: TrackAttrs,
  preset?: GakkiPreset,
): LedgerTrack {
  ensureMixerMaster(t);
  const strip = t.create("mixerChannel", {
    // Input gain at 0 dB, not Audiotool's −8 dB default: the fader carries the
    // lane's whole level (read.ts/pull-plan read the two in series), so a seed
    // plays as loud in Audiotool as it does in Live (tools/audiotool-export-spike.ts).
    preGain: 1,
    displayParameters: { displayName: attrs.displayName, orderAmongStrips: nextStripOrder(t) },
    faderParameters: { postGain: attrs.gain, panning: attrs.panning, isMuted: attrs.muted, isSoloed: attrs.soloed },
  });
  if (kind === "audio") {
    const device = t.create("audioDevice", { displayName: attrs.displayName, gain: 1 });
    const track = t.create("audioTrack", { player: device.location, orderAmongTracks: order });
    t.create("desktopAudioCable", { fromSocket: device.fields.audioOutput.location, toSocket: strip.fields.audioInput.location });
    return { trackId: track.id, trackKind: kind, order, attrs, playerId: device.id, stripId: strip.id };
  }
  // A GM preset when one was fetched, the stand-in synth otherwise. The
  // preset carries its own name, so the lane's is written over it afterwards.
  const synth = preset ? t.createDeviceFromPreset(preset) : t.create(MIDI_STAND_IN_INSTRUMENT, { displayName: attrs.displayName });
  if (preset) t.update(synth.fields.displayName, attrs.displayName);
  const track = t.create("noteTrack", { player: synth.location, orderAmongTracks: order });
  t.create("desktopAudioCable", { fromSocket: synth.fields.audioOutput.location, toSocket: strip.fields.audioInput.location });
  return { trackId: track.id, trackKind: kind, order, attrs, playerId: synth.id, stripId: strip.id };
}

function setLaneAttrs(t: TransactionBuilder, lt: LedgerTrack, attrs: TrackAttrs): void {
  const strip = lt.stripId ? t.entities.getEntityAs(lt.stripId, "mixerChannel") : undefined;
  if (strip) {
    const f = strip.fields.faderParameters.fields;
    // The fader carries the whole level — a channel made in the Studio has the
    // −8 dB input default, which would otherwise sit under every push.
    if (strip.fields.preGain.value !== 1) t.update(strip.fields.preGain, 1);
    t.update(f.postGain, attrs.gain);
    t.update(f.panning, attrs.panning);
    t.update(f.isMuted, attrs.muted);
    t.update(f.isSoloed, attrs.soloed);
    t.update(strip.fields.displayParameters.fields.displayName, attrs.displayName);
  }
  const player = lt.playerId
    ? t.entities.getEntityAs(lt.playerId, "audioDevice", MIDI_STAND_IN_INSTRUMENT, MIDI_PRESET_INSTRUMENT)
    : undefined;
  if (player) t.update(player.fields.displayName, attrs.displayName);
}

function createAudioRegion(
  t: TransactionBuilder,
  lt: LedgerTrack,
  i: Extract<RegionIntent, { kind: "audioRegion" }>,
  library: SampleLibrary,
): string {
  const sample = library.byTag[i.tag];
  if (!sample) throw new Error(`sample ${i.tag} isn't uploaded`);
  const track = t.entities.getEntityAs(lt.trackId, "audioTrack");
  if (!track) throw new Error(`audio track ${lt.trackId} is gone`);
  const region = t.insertSample(
    { name: sample.name, durationSeconds: sample.durationSeconds },
    {
      attachTo: track,
      region: { positionTicks: i.positionTicks, durationTicks: i.durationTicks },
      sample: { musicDurationTicks: i.musicDurationTicks, offsetTicks: i.offsetTicks },
      loop: i.loop === false ? false : { startTicks: i.loop.startTicks, durationTicks: i.loop.durationTicks },
    },
  );
  const rf = region.fields.region.fields;
  t.update(rf.isEnabled, i.isEnabled);
  t.update(rf.displayName, i.displayName);
  t.update(region.fields.fadeInDurationTicks, 0);
  t.update(region.fields.fadeOutDurationTicks, 0);
  return region.id;
}

function createNoteRegion(t: TransactionBuilder, lt: LedgerTrack, i: Extract<RegionIntent, { kind: "noteRegion" }>): string {
  const track = t.entities.getEntityAs(lt.trackId, "noteTrack");
  if (!track) throw new Error(`note track ${lt.trackId} is gone`);
  const collection = t.create("noteCollection", {});
  const region = t.create("noteRegion", {
    track: track.location,
    collection: collection.location,
    region: {
      positionTicks: i.positionTicks,
      durationTicks: i.durationTicks,
      loopOffsetTicks: 0,
      loopDurationTicks: i.durationTicks,
      collectionOffsetTicks: 0,
      isEnabled: i.isEnabled,
      displayName: i.displayName,
    },
  });
  for (const n of i.notes) {
    t.create("note", {
      collection: collection.location,
      positionTicks: n.positionTicks,
      durationTicks: n.durationTicks,
      pitch: n.pitch,
      velocity: n.velocity,
    });
  }
  return region.id;
}

function updateRegion(t: TransactionBuilder, regionId: string, i: RegionIntent): void {
  const e = t.entities.getEntityAs(regionId, "audioRegion", "noteRegion");
  if (!e) throw new Error(`region ${regionId} is gone`);
  const rf = e.fields.region.fields;
  // Growing a region past its loop window would start repeating it; an
  // unlooped region's window is always its whole length.
  const unlooped = i.kind === "noteRegion" || i.loop === false;
  t.update(rf.positionTicks, i.positionTicks);
  t.update(rf.durationTicks, i.durationTicks);
  if (unlooped) t.update(rf.loopDurationTicks, i.durationTicks);
  t.update(rf.isEnabled, i.isEnabled);
  t.update(rf.displayName, i.displayName);
}

function removeRegion(t: TransactionBuilder, regionId: string): void {
  const audio = t.entities.getEntityAs(regionId, "audioRegion");
  if (audio) {
    const sampleId = audio.fields.sample.value.entityId;
    const automationId = audio.fields.playbackAutomationCollection.value.entityId;
    t.remove(audio);
    // Events point INTO the collection, so removing it with dependencies takes them too.
    if (t.entities.has(automationId)) t.removeWithDependencies(automationId);
    const stillUsed = t.entities.ofTypes("audioRegion").get().some((r) => r.fields.sample.value.entityId === sampleId);
    if (!stillUsed && t.entities.has(sampleId)) t.remove(sampleId);
    return;
  }
  const notes = t.entities.getEntityAs(regionId, "noteRegion");
  if (notes) {
    // The region and every note point into the collection.
    t.removeWithDependencies(notes.fields.collection.value.entityId);
    return;
  }
  throw new Error(`region ${regionId} is gone`);
}
